import type {
  ReplaceInvoiceAssigneesMutationOptions,
  ReplaceInvoiceAssigneesVariables,
} from './types';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { queryKeysInvoice } from '../queryKeys';
import { zodInvoiceDetailSchema } from '../useQueryInvoiceDetail/validations';
import { zodInvoicesV2ResponseSchema } from '../useQueryInvoicesV2/validations';

import { replaceInvoiceAssignees } from './replaceInvoiceAssignees';

/**
 * React Query mutation for replacing all invoice assignees.
 *
 * @param options - Optional mutation configuration.
 * @returns Mutation state and methods.
 * @see https://app.dev.vestigas.com/redoc#tag/Invoices/operation/replace_invoice_assignees_invoices__invoice_id__assignees_put
 */
export function useMutationReplaceInvoiceAssignees(
  options?: ReplaceInvoiceAssigneesMutationOptions,
) {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: (variables: ReplaceInvoiceAssigneesVariables) =>
      replaceInvoiceAssignees(variables),
    onSettled(data, error, variables, onMutateResult, context) {
      const detailQueryKey = queryKeysInvoice.getDetail(variables.invoiceId);
      const listQueryFilters = { queryKey: queryKeysInvoice.getAllV2({}) };
      const parsedInvoiceDetail = zodInvoiceDetailSchema.safeParse(data);
      const delegate = () =>
        options?.onSettled?.(data, error, variables, onMutateResult, context);

      if (!parsedInvoiceDetail.success) {
        // Nothing cacheable came back, so settle only after both queries have
        // refetched.
        return Promise.all([
          queryClient.invalidateQueries({ queryKey: detailQueryKey }),
          queryClient.invalidateQueries(listQueryFilters),
        ]).then(delegate);
      }

      const { assignees } = parsedInvoiceDetail.data;

      queryClient.setQueryData(detailQueryKey, parsedInvoiceDetail.data);
      queryClient.setQueriesData<unknown>(
        listQueryFilters,
        (cached: unknown) => {
          const parsedInvoices = zodInvoicesV2ResponseSchema.safeParse(cached);

          // A cached list body that fails validation stays as it is; the list
          // refetch below replaces it.
          if (!parsedInvoices.success) {
            return cached;
          }

          return {
            ...parsedInvoices.data,
            data: parsedInvoices.data.data.map((invoice) =>
              invoice.id === variables.invoiceId
                ? { ...invoice, assignees }
                : invoice,
            ),
          };
        },
      );
      // Not awaited: the mutation stays pending until this callback resolves.
      void queryClient.invalidateQueries(listQueryFilters);

      return delegate();
    },
  });
}
